import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ReactModal from 'react-modal'
import { FormattedMessage } from 'react-intl'
import Button from 'components/Button'
import CloseIcon from 'assets/icons/close-icon'
import RefreshIcon from 'assets/icons/refresh-icon'
import RunReportIcon from 'assets/icons/run-reports-icon'
import Accordion from 'components/Accordion'
import { getFiltersCountries, getFiltersEcoregion, getFiltersRegions } from 'api/filters'
import { requestGetAllEcosystems } from 'api/ecoSystem'
import { requestIUCNListSearch } from 'api/iucnList'
import FilterHeader from './components/filterHeader'
import SiteFiltersContent from './components/siteFiltersContent'
import SpeciesFiltersContent from './components/speciesFiltersContent'
import EcosystemFiltersContent from './components/ecosystemFiltersContent'
import EcologicalFiltersContent from './components/ecologicalFiltersContent'
import { getFiltersCount } from 'pages/Reports'
import {
  FiltersContainer,
  ReportsModalContainer, ReportsModalFooter, ReportsModalFooterBtnWrapper, ReportsModalFooterText,
  ReportsModalHeader, ReportsModalSubTitle,
  ReportsModalSubTitleWrapper,
  ReportsModalTitle,
  ReportsModalToggleWrapper
} from './style'


const customStyles = {
  content: {
    padding: '0',
    top: '0',
    left: 'auto',
    right: '0',
    bottom: 'auto',
    border: 'none',
    borderRadius: 'unset',
    boxShadow: '0px 1px 2px rgba(32, 32, 32, 0.05)'
  },
  overlay: {
    backgroundColor: 'rgba(32, 32, 32, 0.5)',
    zIndex: 1000
  }
}

const filtersData = {
  siteFilters: {
    counter: 9,
    headerTitle: 'siteFilters'
  },
  speciesFilters: {
    counter: 1,
    headerTitle: 'speciesFilters'
  },
  ecosystemFilters: {
    counter: 2,
    headerTitle: 'ecosystemFilters'
  },
  ecologicalFilters: {
    counter: 1,
    headerTitle: 'ecologicalFilters'
  }
}



export const getSelectedOptions = (list, options) => {
  return options.filter((option) => {
    return list.includes(option.value)
  })
}

const compareByName = (a, b) => {
  if (a.name < b.name) return -1
  if (a.name > b.name) return 1
  return 0
}

const compareById = (a, b) => {
  return a.id - b.id
}

const mapSpecieToOption = (specie) => {
  return {
    label: specie.scientific_name,
    value: specie.taxonid
  }
}

const mapEcoregionToOption = (element) => {
  return {
    label: element.name,
    value: element.id
  }
}

const mapCountryToRegionOption = (element) => {
  return {
    label: element.name,
    value: element.id,
    code: element.code
  }
}

const mapRegionToOption = (option) => {
  return {
    label: option.name,
    value: option.id,
    countries: option.Countries.map(mapCountryToRegionOption)
  }
}

const mapCountryToOption = (option) => {
  return {
    label: option.name,
    value: option.id,
    code: option.code,
    region: option.Region.name,
    regionId: option.Region.id
  }
}

const FiltersModal = ({
  isOpen,
  label,
  toggleModal,
  subTitleLabel = 'filtersPanel',
  setFilterCount,
  filterCount,
  filters,
  setFilters,
  runReports,
  defaultFilters
}) => {
  const [closeAllFilters, setCloseAllFilters] = useState(false)
  const [defaultRegionsOptions, setDefaultRegionsOptions] = useState([])
  const [defaultCountryOptions, setDefaultCountryOptions] = useState([])
  const [ecosystemOptions, setEcosystemOptions] = useState([])
  const [ecoregionOptions, setEcoregionOptions] = useState([])
  const [specieOptions, setSpecieOptions] = useState(filters.speciesFilters.selectedSpecieOptions)

  const getSpecieOptions = (query) => {
    const excludeId = getSelectedOptions(filters.speciesFilters.speciesList, specieOptions).map((item) => {
      return item.value
    })
    requestIUCNListSearch(query, true, excludeId).then((data) => {
      const result = data.map(mapSpecieToOption)
      setSpecieOptions(result.concat(getSelectedOptions(filters.speciesFilters.speciesList, specieOptions)))
    })
  }

  const getEcoregionOptions = () => {
    getFiltersEcoregion().then((data)=>{
      const result = data.sort(compareByName).map(mapEcoregionToOption)

      setEcoregionOptions(result)
    })
  }

  const getEcosystemsOptions = () => {
    requestGetAllEcosystems().then((data) => {
      const result = data.map((item, index) => {
        return {
          label: item.name,
          value: index + 1
        }
      })
      setEcosystemOptions(result)
    })
  }

  const getCountriesOptions = () => {
    getFiltersCountries().then((data) => {
      const options = data.map(mapCountryToOption)
      setDefaultCountryOptions([...options])
    })
  }

  const getRegionsOptions = () => {
    getFiltersRegions().then((data) => {
      const options = data.sort(compareById).map(mapRegionToOption)
      setDefaultRegionsOptions([...options])
    })
  }

  const handleCloseModal = () => {
    const currentFilters = JSON.parse(localStorage.getItem('reportsFilters'))
    setFilters(currentFilters)
    setFilterCount(getFiltersCount(currentFilters))
    toggleModal()
  }

  const clearFilters = () => {
    setFilters(defaultFilters)
    localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
    setCloseAllFilters(true)
    setFilterCount(0)
    setSpecieOptions([])
  }

  const handleRunReports = () => {
    runReports()
    toggleModal()
  }

  const updateFilters = useCallback((e, filterName) => {
    const updatedFilters = {...filters, [filterName]: e}

    setFilters(updatedFilters)
    setCloseAllFilters(false)
    setFilterCount(getFiltersCount(updatedFilters))
  },[filters])

  useEffect(() => {
    getCountriesOptions()
    getRegionsOptions()
    getEcosystemsOptions()
    getEcoregionOptions()
  }, [])

  return (
    <ReactModal
      isOpen={isOpen}
      style={customStyles}
      shouldCloseOnOverlayClick={true}
    >
      <ReportsModalHeader>
        <ReportsModalTitle>
          <FormattedMessage id={label} />
        </ReportsModalTitle>
        <ReportsModalToggleWrapper onClick={handleCloseModal}>
          <CloseIcon />
        </ReportsModalToggleWrapper>
      </ReportsModalHeader>
      <FiltersContainer className="scroll">
        <ReportsModalSubTitleWrapper>
          <ReportsModalSubTitle>
            <FormattedMessage id={subTitleLabel} />
          </ReportsModalSubTitle>
        </ReportsModalSubTitleWrapper>
        <ReportsModalContainer>
          <Accordion
            dataTest="Site Filters"
            headerComponent={FilterHeader}
            contentComponent={SiteFiltersContent}
            headerProps={{counter: filtersData.siteFilters.counter, headerTitle: filtersData.siteFilters.headerTitle, closeAllFilters}}
            contentProps={{
              updateFilters,
              filterTitle: filtersData.siteFilters.headerTitle,
              defaultRegionsOptions,
              defaultCountryOptions
            }}
            items={[filters.siteFilters]}
            initialActive={null}
          />
          <Accordion
            dataTest="Species Filters"
            headerComponent={FilterHeader}
            contentComponent={SpeciesFiltersContent}
            headerProps={{counter: filtersData.speciesFilters.counter, headerTitle: filtersData.speciesFilters.headerTitle, closeAllFilters}}
            contentProps={{updateFilters, filterTitle: filtersData.speciesFilters.headerTitle, getSpecieOptions, specieOptions, setSpecieOptions}}
            items={[filters.speciesFilters]}
            initialActive={null}
          />
          <Accordion
            dataTest="Ecosystem Filters"
            headerComponent={FilterHeader}
            contentComponent={EcosystemFiltersContent}
            headerProps={{counter: filtersData.ecosystemFilters.counter, headerTitle: filtersData.ecosystemFilters.headerTitle, closeAllFilters}}
            contentProps={{updateFilters, filterTitle: filtersData.ecosystemFilters.headerTitle, ecosystemOptions}}
            items={[filters.ecosystemFilters]}
            initialActive={null}
          />
          <Accordion
            dataTest="Ecological integrity Filters"
            headerComponent={FilterHeader}
            contentComponent={EcologicalFiltersContent}
            headerProps={{counter: filtersData.ecologicalFilters.counter, headerTitle: filtersData.ecologicalFilters.headerTitle, closeAllFilters}}
            contentProps={{updateFilters, filterTitle: filtersData.ecologicalFilters.headerTitle, ecoregionOptions}}
            items={[filters.ecologicalFilters]}
            initialActive={null}
          />
        </ReportsModalContainer>
      </FiltersContainer>
      <ReportsModalFooter>
        <ReportsModalFooterText>
          <span>{filterCount}</span>
          <FormattedMessage id="filtersApplied"/>
        </ReportsModalFooterText>
        <ReportsModalFooterBtnWrapper>
          <Button dataTest='clear-filters' type="secondary" marginTop="0" height="48px" onClick={clearFilters}>
            <RefreshIcon />
            <FormattedMessage id="clearFilters" />
          </Button>
          <Button dataTest='run-report' type="primary" marginTop="0" height="48px" onClick={handleRunReports}>
            <RunReportIcon />
            <FormattedMessage id="runReport" />
          </Button>
        </ReportsModalFooterBtnWrapper>
      </ReportsModalFooter>
    </ReactModal>
  )
}

const selectedSpecieOptionShape = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.number
})

const siteFiltersShape = PropTypes.shape({
  region: PropTypes.arrayOf(PropTypes.number),
  country: PropTypes.arrayOf(PropTypes.number),
  system: PropTypes.array,
  threats: PropTypes.arrayOf(PropTypes.number),
  assessmentStatus: PropTypes.arrayOf(PropTypes.number),
  kbaStatus: PropTypes.arrayOf(PropTypes.number),
  kbaClassification: PropTypes.arrayOf(PropTypes.number),
  kbaCriterias: PropTypes.arrayOf(PropTypes.number),
  dateOfMostRecentAssessment: PropTypes.arrayOf(PropTypes.number),
  url: PropTypes.string
})

const speciesFiltersShape = PropTypes.shape({
  speciesList: PropTypes.arrayOf(PropTypes.number),
  selectedSpecieOptions: PropTypes.arrayOf(selectedSpecieOptionShape),
  url: PropTypes.string
})

const ecosystemFiltersShape = PropTypes.shape({
  ecosystemList: PropTypes.arrayOf(PropTypes.number),
  ecosystemRedListCategory: PropTypes.array,
  url: PropTypes.string
})

const ecologicalFiltersShape = PropTypes.shape({
  ecoregionList: PropTypes.arrayOf(PropTypes.number),
  url: PropTypes.string
})

FiltersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  toggleModal: PropTypes.func.isRequired,
  subTitleLabel: PropTypes.string,
  setFilterCount: PropTypes.func.isRequired,
  filterCount: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    siteFilters: siteFiltersShape.isRequired,
    speciesFilters: speciesFiltersShape.isRequired,
    ecosystemFilters: ecosystemFiltersShape.isRequired,
    ecologicalFilters: ecologicalFiltersShape.isRequired
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  runReports: PropTypes.func.isRequired,
  defaultFilters: PropTypes.shape({
    siteFilters: siteFiltersShape,
    speciesFilters: speciesFiltersShape,
    ecosystemFilters: ecosystemFiltersShape,
    ecologicalFilters: ecologicalFiltersShape,
    showReports: PropTypes.bool
  }).isRequired
}

FiltersModal.defaultProps = {
  subTitleLabel: 'filtersPanel'
}

export default FiltersModal
